import { useState } from 'react';
import { CheckCircle, Circle, ChevronDown, ChevronUp, X } from 'lucide-react';

export interface ChecklistItem {
  id: string;
  label: string;
  done: boolean;
  link?: string;
}

interface OnboardingChecklistProps {
  items: ChecklistItem[];
  onDismiss?: () => void;
}

export default function OnboardingChecklist({ items, onDismiss }: OnboardingChecklistProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const completedCount = items.filter((item) => item.done).length;
  const totalCount = items.length;
  const progress = (completedCount / totalCount) * 100;
  const isComplete = completedCount === totalCount;

  if (isComplete && onDismiss) {
    return null; // Auto-hide when complete
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-lg"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">Getting Started</span>
            <span className="text-xs text-gray-500">
              {completedCount} of {totalCount} complete
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onDismiss && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDismiss();
              }}
              className="p-1 hover:bg-gray-200 rounded"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          )}
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </div>
      </button>

      {/* Progress Bar */}
      <div className="px-4">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-green-500 h-2 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Checklist Items */}
      {isExpanded && (
        <div className="px-4 py-3 space-y-2">
          {items.map((item) => (
            <div key={item.id} className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {item.done ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <Circle className="w-5 h-5 text-gray-300" />
                )}
              </div>
              {item.link ? (
                <a
                  href={item.link}
                  className={`text-sm flex-1 hover:text-primary-600 transition-colors ${
                    item.done
                      ? 'text-gray-500 line-through'
                      : 'text-gray-900 font-medium'
                  }`}
                >
                  {item.label}
                </a>
              ) : (
                <span
                  className={`text-sm flex-1 ${
                    item.done
                      ? 'text-gray-500 line-through'
                      : 'text-gray-900 font-medium'
                  }`}
                >
                  {item.label}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Celebration Message */}
      {isExpanded && isComplete && (
        <div className="px-4 py-3 bg-green-50 border-t border-green-100 rounded-b-lg">
          <p className="text-sm font-medium text-green-800 text-center">
            🎉 Great job! You've completed the onboarding
          </p>
        </div>
      )}
    </div>
  );
}
